import React, { ReactElement } from 'react';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import ClaimFlowType from 'types/ClaimFlowType';

import { UnderlinedBoldGhostButton } from 'chime-ui/components/Button';
import { useBottomSheet } from 'chime-ui/components/Modal';
import { Superscript, TextBlock, Title } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system/colors';

import PayFAQText from 'components/SubPages/Landing/PayFAQText';

import { useBonusAmount } from 'hooks/useBonusAmount';

import RFAFAQText from './RFAFAQText';

type TermsAndConditionsProps = {
  bank: string;
  claimFlowType: ClaimFlowType;
  isBonusTextVisible?: boolean;
  onFAQClick?: () => void;
};

function TermsAndConditions({
  bank,
  claimFlowType,
  isBonusTextVisible = true,
  onFAQClick,
}: TermsAndConditionsProps): ReactElement {
  const { bonusAmount } = useBonusAmount();
  const {
    Modal: FAQModal,
    isShowing: faqIsShowing,
    handleDismiss: dismissFAQModal,
    handleShow: showFAQModal,
  } = useBottomSheet();

  const handleFAQClick = () => {
    if (onFAQClick) {
      onFAQClick();
    }
    showFAQModal();
  };

  return (
    <Layout>
      <TextBlockLayout>
        <TextBlock size={'small'} color={colors.darkGrey}>
          {isBonusTextVisible && '* When you join Chime. '}
          {bank === 'bancorp'
            ? 'Banking services provided by The Bancorp Bank, Member FDIC'
            : bank === 'stride'
            ? 'Banking services provided by Stride Bank N.A., Member FDIC'
            : ''}
          . Sometimes instant transfers can be delayed. See the{' '}
          <UnderlinedBoldGhostButton onClick={handleFAQClick}>
            Instant Transfers FAQs
          </UnderlinedBoldGhostButton>{' '}
          for full details.
        </TextBlock>
        {isBonusTextVisible && (
          <TextBlock
            size={'small'}
            color={colors.darkGrey}
            as={'div'}
            marginBottom={'1rem'}
          >
            <Superscript>†</Superscript>
            Terms and Conditions: In order to qualify for and receive the $
            {bonusAmount} reward for claiming a fee-free transfer with Chime,
            the following conditions must be met: you must (1) be part of the
            “Claim with Chime” campaign (the “Promotion”); (2) have received a
            Pay Anyone transfer from a Chime member; (3) not have previously
            opened a Chime Checking Account (“Account”); (4) open the new
            Account as prompted by this webpage to claim your Pay Anyone
            transfer; and (5) have activated your physical Chime Visa Debit Card
            within thirty (30) days of opening an Account. Please note, if you
            open an Account independently from the prompt on this webpage, you
            will not be eligible for the reward. Additionally, if you
            successfully join Chime through this Promotion, you and the
            referring Chime member will not be eligible to receive rewards from
            concurrent Chime referral incentive programs, such as the $100
            referral incentive promotion, in addition to the ${bonusAmount}{' '}
            reward for this Promotion. However, you and the referring member are
            eligible to participate in subsequent Chime referral incentive
            programs separate from this Promotion. Pay Anyone transfers expire
            after fourteen (14) days. If your Pay Anyone transfer expires before
            you open an Account, you will not be eligible for the reward. The
            reward will be deposited into your new Checking Account generally
            within two (2) business days after the above conditions are met.
            Limit 1 reward per individual. Chime reserves the right to cancel or
            modify the terms of this reward offer or terminate eligibility at
            any time with or without prior notice. Credits of $10 or more will
            be reported on tax form 1099-INT for new members. Banking services
            are provided by The Bancorp Bank, N.A. or Stride Bank, N.A., Members
            FDIC.
          </TextBlock>
        )}
        <TextBlock size={'small'} color={colors.darkGrey} as={'div'}>
          <Superscript>1</Superscript> Early access to direct deposit funds
          depends on the timing of the submission of the payment file from the
          payer. We generally make these funds available on the day the payment
          file is received, which may be up to 2 days earlier than the scheduled
          payment date.
        </TextBlock>
        <TextBlock size={'small'} color={colors.darkGrey} as={'div'}>
          <Superscript>2</Superscript> Deposits in Chime accounts are FDIC
          insured through The Bancorp Bank or Stride Bank; Members FDIC.
        </TextBlock>
      </TextBlockLayout>

      <AnimatePresence>
        {faqIsShowing && (
          <FAQModal handleDismiss={dismissFAQModal} showX>
            <ModalTitle>
              <Title size={'small'}>
                {claimFlowType == ClaimFlowType.receive
                  ? 'Pay Anyone: Instant Transfer FAQ For Non-Chime Members Senders'
                  : 'Pay Friends Instant Transfer FAQ For Non-Chime Members'}
              </Title>
            </ModalTitle>
            {claimFlowType == ClaimFlowType.receive ? (
              <RFAFAQText />
            ) : (
              <PayFAQText />
            )}
          </FAQModal>
        )}
      </AnimatePresence>
    </Layout>
  );
}

const Layout = styled.div`
  padding-bottom: 1.5rem;
`;

const TextBlockLayout = styled.div`
  display: grid;
  grid-row-gap: 1rem;
`;

const ModalTitle = styled.div`
  padding-right: 1.5rem;
  min-height: 1.75rem;
  margin-bottom: 0.5rem;
`;

export default TermsAndConditions;
